import produce from 'immer';
import { useReducer, useState } from 'react';
import GoogleLogin from 'react-google-login';
import { Link, useHistory } from 'react-router-dom';

import { google_client_id } from 'config';
import { roles, storeToken } from 'utils/handler';
import google from '../assets/img/google-icon.svg';
import logo from '../assets/img/saw-logo.png';
import * as authAction from '../store/actions/auth.action';

interface loginForm {
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface ReducerAction {
  type: string;
  name: string;
  payload?: string | boolean | Partial<loginForm>;
}
const initialState = {
  email: '',
  password: '',
  confirmPassword: '',
  terms: false,
};

const loginReducer = produce((state: loginForm, action: ReducerAction) => {
  const { type, name, payload } = action;
  switch (type) {
    case 'UPDATE':
      state = Object.assign(state, payload);
      return;
    case 'ON_TYPE':
      state[name] = payload;
      return;
    default:
      return;
  }
});

const errorReducer = produce((state: loginForm, action: ReducerAction) => {
  const { type, name, payload } = action;
  switch (type) {
    case 'ADD_ERROR':
      state[name] = payload;
      return;
    case 'DELETE_ERROR':
      state[name] = '';
      return;
    default:
      return;
  }
});

const SignUpPage = () => {
  const [loginDetails, setLoginDetails] = useReducer(loginReducer, initialState);
  const [error, setError] = useReducer(errorReducer, initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);
  const history = useHistory();

  const validation = {
    password(value: string) {
      const empty = !value || !value.trim();
      if (empty) setError({ type: 'ADD_ERROR', name: 'password', payload: "Password can't be empty." });
      else setError({ type: 'DELETE_ERROR', name: 'password' });
      if (empty) return true;
      else return false;
    },
    confirmPassword(value: string, pwd: string) {
      const empty = !value || !value.trim();
      const isNotMatch = value.trim() !== pwd.trim();
      if (empty) setError({ type: 'ADD_ERROR', name: 'confirmPassword', payload: "Confirm password can't be empty." });
      else if (isNotMatch) setError({ type: 'ADD_ERROR', name: 'confirmPassword', payload: 'Confirm password is not match.' });
      else setError({ type: 'DELETE_ERROR', name: 'confirmPassword' });
      if (empty || isNotMatch) return true;
      else return false;
    },
    email(value: string) {
      const empty = !value || !value.trim();
      const isValid = value && !/^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(value);
      if (empty) setError({ type: 'ADD_ERROR', name: 'email', payload: "Email address can't be empty." });
      else if (isValid) setError({ type: 'ADD_ERROR', name: 'email', payload: 'Email address is not valid.' });
      else setError({ type: 'DELETE_ERROR', name: 'email' });
      if (empty || isValid) return true;
      else return false;
    },
    terms(value: boolean) {
      if (!value) setError({ type: 'ADD_ERROR', name: 'terms', payload: 'Please accept privacy policy & terms of service.' });
      else setError({ type: 'DELETE_ERROR', name: 'terms' });
      if (!value) return true;
      else return false;
    },
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { email, password, confirmPassword, terms } = loginDetails;

    let error = false;
    const emailError = validation.email(email);
    if (emailError) error = true;
    const passwordError = validation.password(password);
    if (passwordError) error = true;
    const confirmPasswordError = validation.confirmPassword(confirmPassword, password);
    if (confirmPasswordError) error = true;
    const termsError = validation.terms(terms);
    if (termsError) error = true;

    if (error) return;

    authAction.signup({ password, email, confirmPassword }).then((res: any) => {
      if (res)
        storeToken(res).then(() => {
          history.push('/');
        });
    });
  };

  const responseGoogle = async (data: any) => {
    if (data.profileObj) {
      const profile = data.profileObj;
      const payload = {
        email: profile.email,
        provider_id: profile.googleId,
        provider_name: 'Google',
        image: profile.imageUrl,
        name: profile.name,
        roleType: roles.buyer,
      };
      const res: string = await authAction.socialLogin(payload);
      if (res)
        storeToken(res).then(() => {
          history.push('/');
        });
    }
  };

  return (
    <div className="login_bg ">
      <div className="container">
        <div className="pt-4 login_logo">
          <img alt="logo-png" src={logo} width={150} height={60} className="c-pointer" onClick={() => history.push('/')} />
        </div>

        <div className="login_wrapper">
          <div className="d-flex align-items-baseline justify-content-center">
            <div className="login_head">Sign Up</div>
            <div className="login_head_buy">as a Buyer</div>
          </div>

          <div>
            <input
              className={`saw-input form-control ${error.email ? 'is-invalid' : ''}`}
              type="email"
              placeholder="Email"
              value={loginDetails.email}
              autoComplete="email"
              onChange={(e) => {
                const value = e.target.value;
                setLoginDetails({ type: 'ON_TYPE', name: 'email', payload: value });
                validation.email(value);
              }}
            />
            {error.email && <div className="login_form_error">{error.email}</div>}
          </div>
          <div className=" password-container">
            <input
              className={`saw-input form-control ${error.password ? 'is-invalid' : ''}`}
              type={showPassword ? 'text' : 'password'}
              value={loginDetails.password}
              placeholder="Password"
              autoComplete="new-password"
              onChange={(e) => {
                const value = e.target.value;
                setLoginDetails({ type: 'ON_TYPE', name: 'password', payload: value });
                validation.password(value);
              }}
            />
            {loginDetails.password && <i className={'password-eye fa-lg theme-color c-pointer fa fa-eye' + (!showPassword ? '' : '-slash')} onClick={() => setShowPassword((v) => !v)} aria-hidden="true"></i>}
            {error.password && <div className="login_form_error">{error.password}</div>}
          </div>
          <div className=" password-container">
            <input
              className={`saw-input form-control ${error.confirmPassword ? 'is-invalid' : ''}`}
              type={showCPassword ? 'text' : 'password'}
              value={loginDetails.confirmPassword}
              placeholder="Confirm password"
              autoComplete="off"
              onChange={(e) => {
                const value = e.target.value;
                setLoginDetails({ type: 'ON_TYPE', name: 'confirmPassword', payload: value });
                validation.confirmPassword(value, loginDetails.password);
              }}
            />
            {loginDetails.confirmPassword && <i className={'password-eye fa-lg theme-color c-pointer fa fa-eye' + (!showCPassword ? '' : '-slash')} onClick={() => setShowCPassword((v) => !v)} aria-hidden="true"></i>}
            {error.confirmPassword && <div className="login_form_error">{error.confirmPassword}</div>}
          </div>

          <div>
            <div className="d-flex">
              <input
                type="checkbox"
                className="flex-shrink-0"
                id="sign_up_terms_accept"
                checked={loginDetails.terms}
                onChange={() => {
                  const value = !loginDetails.terms;
                  setLoginDetails({ type: 'ON_TYPE', name: 'terms', payload: value });
                  validation.terms(value);
                }}
              />
              <label htmlFor="sign_up_terms_accept" className="c-pointer">
                I agree to Saw.com{' '}
                <a href="https://saw.com/privacy-policy/">
                  <span className="sign_up_terms">Privacy Policy</span>
                </a>{' '}
                &{' '}
                <a href="https://saw.com/terms-of-service#buyer-brokerage-services">
                  <span className="sign_up_terms">Terms of Service</span>
                </a>
              </label>
            </div>
            {error.terms && <div className="sell_form_error d-flex justify-content-center">{error.terms}</div>}
          </div>

          <button className="login_button" onClick={handleFormSubmit}>
            Sign up
          </button>

          <GoogleLogin
            clientId={google_client_id}
            onSuccess={responseGoogle}
            render={(prop) => (
              <button className="login_with_google" onClick={prop.onClick} disabled={prop.disabled}>
                <span>
                  <img src={google} alt="" width={25} />
                </span>
                <span className="ml-2">Sign up with Google</span>
              </button>
            )}
          />

          <div className="d-flex justify-content-center fs_14">
            <Link to="/login">
              <div className="login_text_action">Already have an account?</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
